import {Modal} from 'antd'
import {course_getCourseClasses_Api, courseEnrollment_getById_Api,} from 'apis'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {CourseActionTypes, enrollment_status_options,} from 'views/AppLocals/Course'
import {useCourse} from 'views/AppLocals/Course/useCourse'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {CourseApproveModal} from '../components/CourseApproveModal'
import {EnrollmentReviewDrawer} from '../components/EnrollmentReviewDrawer'
import {WalkInEnrollmentModal} from '../components/WalkInEnrollmentModal'
import {CourseEnrollmentFilterByCourse} from '../components/filters/CourseEnrollmentFilter'
import {renderCourseEnrollment} from '../functions/renderCourseEnrollment'
import {course_getCourseEnrollments_Api} from "../../../../apis";
import {IoWarning} from "react-icons/io5";
import {copyLocationParam, getRouteSearchParams, useRouteSearchParam} from "../../../../envs/_current/history";
import {bindingSearch} from "../../../Shared/components/PagerControls";
import {pagerControls, renderIf} from "../../../Shared";
import {PiCheckCircle, PiMagnifyingGlass, PiXCircle} from "react-icons/pi";
import classNames from "classnames";
import {BiTrash} from "react-icons/bi";
import LoadingBlock from "../../../Loading/LoadingBlock";

const PIPELINE_STEPS = [
    {
        step: 1,
        labelKey: 'walk in enrollment',
        descKey: 'pipeline step 1 desc',
        statusValue: -1,
        color: 'text-blue-500',
        bg: 'bg-blue-50 border-blue-200',
    },
    {
        step: 2,
        labelKey: 'identity review',
        descKey: 'pipeline step 2 desc',
        statusValue: 1,
        color: 'text-purple-500',
        bg: 'bg-purple-50 border-purple-200',
    },
    {
        step: 3,
        labelKey: 'ready for class',
        descKey: 'pipeline step 3 desc',
        statusValue: 2,
        color: 'text-orange-500',
        bg: 'bg-orange-50 border-orange-200',
    },
    {
        step: 4,
        labelKey: 'enrollment completed',
        descKey: 'pipeline step 4 desc',
        statusValue: 9,
        color: 'text-green-600',
        bg: 'bg-green-50 border-green-200',
    },
]

const EnrollmentPipelineGuide = () => {
    const t = useTranslate()
    return (
        <div className="rounded-xl border border-color-50 background p-3 flex flex-col gap-2">
            <span className=" font-semibold text-gray-500 uppercase tracking-wider">
                {t('enrollment process guide')}
            </span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PIPELINE_STEPS.map(({step, labelKey, descKey, color, bg}) => (
                    <div
                        key={step}
                        className={classNames(
                            'flex flex-col gap-1 p-2.5 rounded-lg border',
                            bg
                        )}>
                        <div className="flex items-center gap-1.5">
                            <span className={classNames(
                                'w-5 h-5 flex flex-center flex-shrink-0 rounded-full font-bold text-2xs text-white',
                                step === 4 ? 'bg-green-500' : 'bg-blue-400'
                            )}>
                                {step}
                            </span>
                            <span className={classNames('text-xs font-semibold leading-snug', color)}>
                                {t(labelKey)}
                            </span>
                        </div>
                        <span className="text-2xs text-color-400 italic leading-snug tracking-wider">
                            {t(descKey)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const CourseEnrollmentsByCourse = ({
                                       showHeader = true,
                                       course_id,
                                       subjects = [],
                                       controls = pagerControls,
                                       filter_actions = {
                                           status: 'enrollment-status',
                                           sortBy: 'sort-by',
                                           keyword: 'keyword'
                                       },
                                   }) => {
    const t = useTranslate()

    const history = useHistory()

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)

    const {keyword, component: search_widget} = controls?.search ?? {}
    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const status = useRouteSearchParam(filter_actions.status)

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const [reviewEnrollment, setReviewEnrollment] = useState(null)
    const [courseClasses, setCourseClasses] = useState([])
    const [refreshToken, setRefreshToken] = useState(0)
    const [walkInOpen, setWalkInOpen] = useState(false)

    const {handleAsyncAction: loadCourseClasses} = useAsyncAction({
        apiInfo: course_getCourseClasses_Api,
        onSuccess: (result, meta) => {
            setCourseClasses(_.get(meta, 'response.data.data', []))
        },
    })

    useEffect(() => {
        if (course_id) {
            loadCourseClasses({}, {id: course_id})
        }
    }, [course_id])

    let timeout
    useEffect(() => {
        const searchParams = getRouteSearchParams()
        searchParams.set(
            filter_actions.status,
            enrollment_status_options.pending.value
        )
        timeout = setTimeout(() => {
            history.replace({
                ...copyLocationParam(window.location ?? {}),
                search: '?' + searchParams.toString(),
            })
        })
        return () => clearTimeout(timeout)
    }, [])


    const {
        refreshKey,
        onReject = NullAsDefault,
        onApprove = NullAsDefault,
        onDeleteEnrollment = NullAsDefault,
    } = useCourse({
        onRejected: onCancel,
        onApproved: onCancel,
        onEnrollmentDeleted: onCancel,
    })

    const confirmReject = (id) => {
        if (id) {
            showDeleteConfirmDialog({
                icon: <IoWarning className="anticon"/>,
                translate: t,
                title: 'reject student',
                message: 'are you sure you want to reject this student',
                onOk: () => {
                    onReject({}, {id})
                },
            })
        }
    }

    const confirmDelete = (id) => {
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                onOk: () => {
                    onDeleteEnrollment({}, {id})
                },
            })
        }
    }

    const handleAction = (action, params) => {
        const course_id = params?.id
        switch (action) {
            case CourseActionTypes.ENROLLMENT_APPROVE:
                setAction({type: action, value: params})
                break
            case CourseActionTypes.ENROLLMENT_REJECT:
                confirmReject(course_id)
                break
            case CourseActionTypes.ENROLLMENT_DELETE:
                confirmDelete(course_id)
                break
            case CourseActionTypes.ENROLLMENT_REVIEW:
                setReviewEnrollment(params)
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback((item) => {
        let included_actions = []
        switch (item?.status) {
            case enrollment_status_options.pending.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REVIEW,
                    CourseActionTypes.ENROLLMENT_REJECT,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            case enrollment_status_options.identity_review.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REVIEW,
                    CourseActionTypes.ENROLLMENT_REJECT,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            case enrollment_status_options.ready_for_class.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REVIEW,
                    CourseActionTypes.ENROLLMENT_REJECT,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            case enrollment_status_options.rejected.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REVIEW,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            case enrollment_status_options.completed.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REJECT,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            default:
                included_actions = [CourseActionTypes.ENROLLMENT_DELETE]
                break
        }
        return createContextMenu(
            [
                {
                    label: 'review',
                    icon: <PiMagnifyingGlass size={14} className="text-blue-500"/>,
                    key: CourseActionTypes.ENROLLMENT_REVIEW,
                },
                {
                    label: 'approve',
                    icon: <PiCheckCircle size={14} className="text-green-500"/>,
                    key: CourseActionTypes.ENROLLMENT_APPROVE,
                },
                {
                    label: 'reject',
                    icon: <PiXCircle size={14} className="text-orange-500"/>,
                    key: CourseActionTypes.ENROLLMENT_REJECT,
                },
                {
                    label: 'delete',
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    key: CourseActionTypes.ENROLLMENT_DELETE,
                },
            ].filter(({key}) => included_actions.includes(key)),
            (key) => {
                handleAction(key, item)
            }
        )
    }, [])

    const renderActions = useCallback((item) => {
        let included_actions = []
        switch (item?.status) {
            case enrollment_status_options.pending.value:
            case enrollment_status_options.identity_review.value:
            case enrollment_status_options.ready_for_class.value:
                included_actions = [CourseActionTypes.ENROLLMENT_REJECT]
                break
            case enrollment_status_options.rejected.value:
                included_actions = []
                break
            case enrollment_status_options.completed.value:
                included_actions = [CourseActionTypes.ENROLLMENT_REJECT]
                break
            default:
                break
        }
        const pipelineStatuses = [-1, 1, 2]
        const showReviewButton = pipelineStatuses.includes(item?.status)

        const legacyButtons = [
            {
                label: 'approve',
                icon: <PiCheckCircle size={13}/>,
                key: CourseActionTypes.ENROLLMENT_APPROVE,
                buttonParams: {
                    type: "default",
                    className: 'bg-green-200 hover:bg-green-500 border border-green-400 hover:border-green-500 text-green-700 hover:text-white',
                }
            },
            {
                label: 'reject',
                icon: <PiXCircle size={13}/>,
                key: CourseActionTypes.ENROLLMENT_REJECT,
                buttonParams: {
                    type: "default",
                    className: 'bg-orange-50 hover:bg-orange-500 border border-orange-500 hover:border-orange-500 text-orange-400 hover:text-white',
                }
            },
        ].filter(({key}) => included_actions.includes(key))
            .map((e, i) => (
                <button
                    key={i}
                    type={e?.buttonParams?.type}
                    style={{padding: '2px 8px', fontSize: '0.75rem', letterSpacing: '0.025em'}}
                    className={classNames("flex flex-center gap-2 button-rounded-md px-2 font-medium", e?.buttonParams?.className)}
                    onClick={() => {
                        handleAction(e?.key, item)
                    }}>
                    {renderIf(!isSm, e?.icon)}
                    {t(e?.label)}
                </button>
            ))

        const reviewButton = showReviewButton ? (
            <button
                key="review"
                style={{padding: '2px 8px', fontSize: '0.75rem', letterSpacing: '0.025em'}}
                className="flex flex-center gap-2 button-rounded-md px-2 font-medium bg-blue-50 hover:bg-blue-500 border border-blue-400 hover:border-blue-500 text-blue-600 hover:text-white"
                onClick={() => setReviewEnrollment(item)}>
                {t('review')}
            </button>
        ) : null

        return [...legacyButtons, reviewButton].filter(Boolean)
    }, [])

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let title
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {title: undefined}
        let modal_params = {}
        switch (action?.type) {
            case CourseActionTypes.ENROLLMENT_APPROVE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('registration review form')}
                    </div>
                )
                params = {
                    onCancel,
                    onSubmit: (values) => {
                        onApprove(values, {id})
                    },
                    onReject: () => {
                        onReject({}, {id})
                    }
                }
                modal_params = {
                    width: 720,
                }
                Content = CourseApproveModal
                Wrapper = ({children}) => (
                    <Async
                        {...{
                            query: bindQueryParam({id}),
                            apiInfo: courseEnrollment_getById_Api,
                        }}>
                        {({isLoading, response}) =>
                            _.isFunction(children)
                                ? children({
                                    isLoading,
                                    initialValues: getResponseItem(response),
                                })
                                : children
                        }
                    </Async>
                )
                break
            default:
                break
        }
        return (
            <Modal
                className="custom-modal"
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                {...modal_params}>
                <Wrapper>
                    {({isLoading, initialValues}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                initialValues={initialValues}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action])

    const refresh_deps = useMemo(
        () => [
            course_id,
            refreshKey,
            JSON.stringify(keyword),
            `refreshToken=${refreshToken}`,
        ],
        [course_id, keyword, refreshKey, refreshToken]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            course_id,
            keyword
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                status,
                keyword
            })
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            renderItem: renderCourseEnrollment({
                renderContextMenu: renderContextMenu,
                renderActions: renderActions,
                statusFilter: status,
                hideGotoLink: true,
            }),
            apiInfo: course_getCourseEnrollments_Api,
            query: bindQueryParams([{id: course_id}]),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        dependencies: [course_id, keyword],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: CourseEnrollmentFilterByCourse,
        renderFilter: (child, filterWidget) => {
            return (
                <div className="mb-2">
                    {filterWidget}
                </div>
            )
        },
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-4 mt-4">
                {/* Pipeline info block */}
                <EnrollmentPipelineGuide/>
                <div className="flex justify-end">
                    <button
                        style={{padding: '4px 14px'}}
                        className="flex flex-center gap-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => setWalkInOpen(true)}>
                        {t('walk in enrollment')}
                    </button>
                </div>
                {filterBar}
                {search_widget}
            </div>
        ),
    })
    return (
        <div className="flex flex-col flex-1 space-y-3 111-111">
            {pager_widget}
            {modal}
            <div className="h-16"/>
            <WalkInEnrollmentModal
                open={walkInOpen}
                courseId={course_id}
                subjects={subjects}
                onClose={() => setWalkInOpen(false)}
                onCreated={(newEnrollment) => {
                    setWalkInOpen(false)
                    setReviewEnrollment(newEnrollment)
                }}
            />
            <EnrollmentReviewDrawer
                open={!!reviewEnrollment}
                enrollment={reviewEnrollment}
                onClose={() => setReviewEnrollment(null)}
                onCompleted={() => {
                    setReviewEnrollment(null)
                    setRefreshToken(t => t + 1)
                }}
                courseClasses={courseClasses}
            />
        </div>
    )
}

export const CourseEnrollmentsByCourseSearchBinding = ({...props}) => bindingSearch(props)(CourseEnrollmentsByCourse)

export default compose(
    branch(
        ({course_id}) => {
            return _.some([course_id], _.isEmpty)
        },
        () => () => <ContentNotFound/>
    )
)(CourseEnrollmentsByCourse)
